import { BellOutlined } from '@ant-design/icons';
import { Badge, Button, Drawer } from 'antd';
import useModal from 'hooks/useModal';
import { noop } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { getNoticeUnreadCount, popupList } from 'services/api/notice';
import nimInst from 'services/nim';
import NoticeList from './NoticeList';
import NoticeModal, { useTypeLabel } from './NoticeModal';
import useNoticeList from './useNoticeList';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { cx } from '@emotion/css';
import { useRequest } from 'ahooks';
import { NoticeDrawer } from './NoticeDrawer';
import { buryNoticeBell } from 'services/etc/bury';
import { eventBus } from 'services/eventBus';

function Notice() {
  const { t: defaultT } = useTranslate();
  const { getTypeLabel } = useTypeLabel();

  const systemLanguage = useSystemLanguageValue();

  const { list, listType, refresh, hasMore, loadMore, syncList, read } = useNoticeList();

  const [drawerVisible, setDrawerVisible] = useState(false);

  const { visible, show, hide, meta } = useModal<Notice.Item>();

  const [renderType, setRenderType] = useState<'drawer' | 'modal'>('modal');

  const { data: unreadCount, runAsync: getUnreadCount } = useRequest(getNoticeUnreadCount);

  const filterOptions: Array<{ label: string; value: Notice.FilterType }> = useMemo(
    () => [
      {
        label: defaultT('feedback.全部'),
        value: 0,
      },
      {
        label: getTypeLabel('新功能'),
        value: 1,
      },
      {
        label: getTypeLabel('推荐配置'),
        value: 5,
      },
      {
        label: getTypeLabel('活动'),
        value: 2,
      },
      {
        label: getTypeLabel('公告'),
        value: 3,
      },
      {
        label: getTypeLabel('问题反馈'),
        value: 4,
      },
      {
        label: getTypeLabel('账单'),
        value: 6,
      },
    ],
    [defaultT, getTypeLabel],
  );

  const popup = useCallback(() => {
    popupList()
      .then((res) => {
        const popupableList = res.filter(
          ({ languages = [] }) => languages.length === 0 || languages.includes(systemLanguage),
        );

        if (popupableList.length === 0) {
          return;
        }

        show(popupableList[0]);
      })
      .catch(noop);
  }, [show, systemLanguage]);

  const refsCurrent = {
    popup,
    syncList,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const handler = () => {
      const {
        current: { syncList, popup },
      } = refs;

      syncList().then(popup);
    };

    handler();

    const offs = [
      nimInst.on('sys:message:pull_notice_msg', handler),
      nimInst.on('sys:message:sync_notice_unread_count', () => {
        getUnreadCount();
      }),
    ];

    return () => {
      offs.forEach((off) => off());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const button = (
    <Button
      type="text"
      onClick={() => {
        refresh(listType);
        buryNoticeBell();
        setDrawerVisible(true);
      }}
    >
      <Badge
        size="small"
        offset={[4, 0]}
        count={unreadCount && unreadCount >= 100 ? '99+' : unreadCount}
      >
        <BellOutlined />
      </Badge>
    </Button>
  );

  const drawer = (
    <Drawer
      bodyStyle={{ padding: 0 }}
      title={defaultT('notificationDialog.通知中心')}
      width={480}
      visible={drawerVisible}
      onClose={() => setDrawerVisible(false)}
    >
      <div className="flex flex-col h-full">
        <div className="flex px-6 py-2 gap-3 border-t-0 border-x-0 border-b border-solid border-fill-200  w-[480px] flex-wrap">
          {filterOptions.map(({ label, value }) => (
            <div
              key={value}
              className={cx(
                'bg-[var(--fett-bg-subtle)] py-1.5 px-4 rounded-full cursor-pointer whitespace-nowrap',
                listType === value ? 'bg-[var(--fett-bg-info-tip)] text-primary' : '',
              )}
              onClick={() => {
                refresh(value);
              }}
            >
              {label}
            </div>
          ))}
        </div>
        <div className="flex-1 py-4">
          {drawerVisible && (
            <NoticeList
              key={listType}
              list={list}
              hasMore={hasMore}
              onLoadMore={loadMore}
              onNoticeSelect={(notice) => {
                if (notice.type === '问题反馈') {
                  if (notice.caseId) {
                    eventBus.emit('SHOW_FEEDBACK_MODAL', notice.caseId);
                    read(notice);
                  }
                  return;
                }
                setRenderType('drawer');
                show(notice);
                read(notice);
              }}
            />
          )}
        </div>
      </div>

      {meta && renderType === 'drawer' && (
        <NoticeDrawer
          notice={meta}
          width={480}
          visible={visible}
          onClose={() => {
            setRenderType('modal');
            hide();
            read(meta);
          }}
        />
      )}
    </Drawer>
  );

  const modal =
    meta && renderType === 'modal' ? (
      <NoticeModal
        notice={meta}
        visible={visible}
        zIndex={2000000}
        onCancel={() => {
          hide();
          read(meta);
        }}
        onCloseDrawer={() => setDrawerVisible(false)}
      />
    ) : null;

  return (
    <>
      {button}
      {drawer}
      {modal}
    </>
  );
}

export default Notice;
